const WINDOWS_TO_IANA_TIMEZONE = {
  'Eastern Standard Time': 'America/New_York',
  'Central Standard Time': 'America/Chicago',
  'Mountain Standard Time': 'America/Denver',
  'Pacific Standard Time': 'America/Los_Angeles',
  UTC: 'UTC'
};

export const getIanaTimeZone = (value = '') => {
  const normalized = String(value || '').trim();
  if (!normalized) return null;
  return WINDOWS_TO_IANA_TIMEZONE[normalized] || normalized;
};

const getDatePartsInTimeZone = (date, timeZone) => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  const parts = formatter.formatToParts(date).reduce((acc, part) => {
    if (part.type !== 'literal') acc[part.type] = Number(part.value);
    return acc;
  }, {});

  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour: parts.hour,
    minute: parts.minute,
    second: parts.second
  };
};

const getUtcDateForWallClockInTimeZone = (components, timeZone) => {
  let utcMillis = Date.UTC(
    components.year,
    components.month - 1,
    components.day,
    components.hour,
    components.minute,
    components.second || 0
  );

  for (let index = 0; index < 2; index += 1) {
    const zonedParts = getDatePartsInTimeZone(new Date(utcMillis), timeZone);
    const zonedMillis = Date.UTC(
      zonedParts.year,
      zonedParts.month - 1,
      zonedParts.day,
      zonedParts.hour,
      zonedParts.minute,
      zonedParts.second || 0
    );
    const targetMillis = Date.UTC(
      components.year,
      components.month - 1,
      components.day,
      components.hour,
      components.minute,
      components.second || 0
    );
    utcMillis += (targetMillis - zonedMillis);
  }

  return new Date(utcMillis);
};

export const parseEventDateTime = (value, eventTimeZone) => {
  if (!value) return null;

  const ianaTimeZone = getIanaTimeZone(eventTimeZone);

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;

    const wallClock = {
      year: value.getUTCFullYear(),
      month: value.getUTCMonth() + 1,
      day: value.getUTCDate(),
      hour: value.getUTCHours(),
      minute: value.getUTCMinutes(),
      second: value.getUTCSeconds()
    };

    return ianaTimeZone
      ? getUtcDateForWallClockInTimeZone(wallClock, ianaTimeZone)
      : new Date(
        wallClock.year,
        wallClock.month - 1,
        wallClock.day,
        wallClock.hour,
        wallClock.minute,
        wallClock.second,
        value.getUTCMilliseconds()
      );
  }

  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (match) {
      const [, year, month, day, hour, minute, second] = match;
      const wallClock = {
        year: Number(year),
        month: Number(month),
        day: Number(day),
        hour: Number(hour),
        minute: Number(minute),
        second: Number(second || 0)
      };

      return ianaTimeZone
        ? getUtcDateForWallClockInTimeZone(wallClock, ianaTimeZone)
        : new Date(
          wallClock.year,
          wallClock.month - 1,
          wallClock.day,
          wallClock.hour,
          wallClock.minute,
          wallClock.second
        );
    }
  }

  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

export const formatEventDateTimeDisplay = (value, eventTimeZone) => {
  const date = parseEventDateTime(value, eventTimeZone);
  if (!date) return '';

  const ianaTimeZone = getIanaTimeZone(eventTimeZone);

  try {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: ianaTimeZone || undefined,
      timeZoneName: ianaTimeZone ? 'short' : undefined
    }).format(date);
  } catch {
    return date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
};